/**
 * Mis facturas (P30) · listado de acuses de validación del solicitante actual
 * + descargas (XML original y PDF del acuse, generado en runtime).
 */
import { axiosClient } from "@/shared/api/axios-client";
import { encolarDescarga } from "@/shared/api/descarga-secuencial";
import type { DocumentoApi } from "@/lib/types";

export interface Factura {
  id: string;
  solicitudId: string;
  numeroSolicitud: number;
  uuid: string;
  serie: string | null;
  folio: string | null;
  rfcEmisor: string;
  nombreEmisor: string;
  rfcReceptor: string;
  nombreReceptor: string;
  fechaValidacion: string;
  fechaTimbrado: string | null;
  fechaEmision: string | null;
  total: number;
  moneda: string;
  tipoComprobante: string;
  estatus: string;
  ordenCompraClave: string | null;
  observaciones: string | null;
  /**
   * Folio completo de la solicitud relacionada · sociedad (4) + número a 11 dígitos
   * (15 caracteres), o solo los 11 dígitos si no hay sociedad. La sociedad es la de la
   * organización confirmada o, de respaldo, la del RFC receptor del CFDI si está en el
   * catálogo (decisión del usuario, 30/09/2026).
   */
  folioSolicitud: string;
}

/**
 * Query params del listado paginado · TODO el filtrado/orden/paginado es
 * server-side. Las fechas son días "YYYY-MM-DD" (Del=Al para filtrar un día);
 * los demás filtros son contains normalizado por columna.
 */
export interface FacturasParams {
  timbradoDel?: string;
  timbradoAl?: string;
  emitidoDel?: string;
  emitidoAl?: string;
  validadoDel?: string;
  validadoAl?: string;
  uuid?: string;
  emisor?: string;
  receptor?: string;
  estatus?: string;
  serie?: string;
  folio?: string;
  ordenCompra?: string;
  tipo?: string;
  total?: string;
  moneda?: string;
  observaciones?: string;
  numDoc?: string;
  sortKey?: string;
  sortDir?: string;
  page?: number;
  pageSize?: number;
}

/** Página del listado · espejo de FacturasPageDto del backend. */
export interface FacturasPage {
  items: Factura[];
  total: number;
  page: number;
  pageSize: number;
}

function buildQueryString(filtros: FacturasParams): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export async function listarMisFacturas(params: FacturasParams = {}): Promise<FacturasPage> {
  const { data } = await axiosClient.get<FacturasPage>(
    `/api/facturas${buildQueryString(params)}`
  );
  return data;
}

/**
 * Facturas (acuses de validación) de UNA solicitud · fila expandible de «Mis
 * solicitudes de pago». El backend devuelve solo el acuse MÁS RECIENTE por UUID
 * y decide el acceso con la política única de la solicitud (dueño · ver todas ·
 * autorizador de la organización); sin acceso responde 404.
 */
export async function listarFacturasDeSolicitud(solicitudId: string): Promise<Factura[]> {
  const { data } = await axiosClient.get<Factura[]>(`/api/facturas/solicitud/${solicitudId}`);
  return data;
}

/**
 * Descarga vía blob · el filename real viene en `Content-Disposition`
 * (mismo patrón que las descargas CSV de auditoría).
 *
 * SECUENCIAL por directiva: las descargas van por la cola global — de una
 * en una, sin paralelas ni ráfagas ni pausas (ver descarga-secuencial.ts).
 */
async function descargarBlob(
  url: string,
  contentType: string,
  fallbackFilename: string
): Promise<void> {
  const response = await encolarDescarga(() =>
    axiosClient.get<Blob>(url, { responseType: "blob" })
  );
  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;
  const blob = new Blob([response.data], { type: contentType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export async function descargarXmlDeFactura(f: Factura): Promise<void> {
  await descargarBlob(`/api/facturas/${f.id}/xml`, "text/xml", `${f.uuid}.xml`);
}

export async function descargarAcusePdf(f: Factura): Promise<void> {
  await descargarBlob(
    `/api/facturas/${f.id}/acuse.pdf`,
    "application/pdf",
    `acuse_${f.numeroSolicitud}_${f.uuid}.pdf`
  );
}

/** Se lanza cuando la solicitud no tiene un PDF de factura cargado. */
export class FacturaPdfNoDisponibleError extends Error {
  constructor() {
    super("La solicitud no tiene un PDF de factura cargado.");
    this.name = "FacturaPdfNoDisponibleError";
  }
}

/**
 * Descarga el PDF de la FACTURA (el CFDI en PDF que subió el solicitante), no el
 * acuse. El acuse (ValidacionFiscal) no conoce el PDF —vive como documento
 * `FacturaPdf` en GestionDocumental—, así que se resuelve en dos pasos: listar
 * los documentos de la solicitud y descargar el `FacturaPdf`. Para solicitudes
 * de MÚLTIPLES CFDIs (varios PDFs) se elige el que corresponde a esta factura
 * casando el UUID en el nombre del archivo; si no casa, cae al único disponible.
 */
export async function descargarFacturaPdf(f: Factura): Promise<void> {
  const { data: docs } = await axiosClient.get<DocumentoApi[]>(
    `/api/solicitudes/${f.solicitudId}/documentos`
  );
  const pdfs = docs.filter((d) => d.categoria === "FacturaPdf");
  if (pdfs.length === 0) throw new FacturaPdfNoDisponibleError();

  const uuidUpper = f.uuid.toUpperCase();
  const elegido =
    pdfs.find((d) => (d.nombreOriginal ?? "").toUpperCase().includes(uuidUpper)) ??
    pdfs[0];

  await descargarBlob(
    `/api/solicitudes/${f.solicitudId}/documentos/${elegido.id}/descargar`,
    "application/pdf",
    elegido.nombreOriginal || `${f.uuid}.pdf`
  );
}
